import _ from 'lodash'
import { combineYear } from 'legacy/services/entry/years'
import * as urls from 'legacy/common/urls'
import { downloadBib } from 'legacy/common/urls'
import api, { formCfg } from './api'
import apiFactory from './api-factory'
import pollProgressApi from './poll-progress'
import { dehydrate as entryTagDehydrate } from './entry-tag'
import { dehydrate as nameDehydrate, hydrate as nameHydrate } from './name'
import { dehydrate as expertDehydrate } from './expert'
import { dehydrate as secondarySourceDehydrate } from './secondary-source'
import {
  dehydrate as dataSourceDehydrate,
  hydrate as dataSourceHydrate,
} from './data-source'
import {
  dehydrate as entrySourceDehydrate,
  hydrate as entrySourceHydrate,
  hydrateSecondarySource,
} from './entry-source'
import {
  dehydrate as referenceDehydrate,
  hydrate as referenceHydrate,
  bibliographyHydrate,
} from './reference'
import { hydrate as answerSetReferenceHydrate } from './answer-set-reference'
import { dehydrate as referenceTypesDehydrate } from './reference-type'
import {
  dehydrate as entrySubReference,
  hydrate as entrySubReferenceHydrate,
  hydrateAnswerSetSubReference as answerSetSubReferenceHydrate,
} from './sub-reference'
import {
  getGoogleBooksUrl,
  cleanGoogleBooks,
  getCrossRefUrl,
  cleanCrossRef,
} from './research'

import { dehydrate as doiDehydrate } from './entry-doi'
import {
  hydrate as regionHydrate,
  dehydrate as regionDehydrate,
  dehydrateAndPopulate as regionDehydrateAndPopulate,
} from './region'

const entry = {
  ...apiFactory(
    '/api/polls/entry/',
    ['getDetail', 'create', 'delete', 'partialUpdate'],
    { hydrate, dehydrate }
  ),

  getList(params, context) {
    return api
      .get('/api/polls/entry/', formCfg(params, context))
      .then((response) => _.map(response.data, (entry) => listDehydrate(entry)))
  },

  getListWithDependencies(dependencies, params, context) {
    return api
      .get('/api/polls/entry/', formCfg(params, context))
      .then((response) =>
        _.map(response.data, (entry) =>
          dehydrateAndPopulate(entry, prepareDependencies(dependencies))
        )
      )
  },

  getStats(pk, params, context) {
    return api
      .get(`/api/polls/entry/${pk}/stats/`, formCfg(params, context))
      .then((response) => dehydrateStats(response.data))
  },

  getDetailWithProgress(pk, params, { expert }) {
    return this.getDetail(pk).then((entry) => {
      const pollPk = entry.poll

      return pollProgressApi
        .getDetail(
          pollPk,
          {},
          {
            poll: pollPk,
            entry: pk,
            expert,
          }
        )
        .then((poll) => ({
          ...entry,
          poll,
        }))
    })
  },

  updateDefaultSettings(pk, data, params, context) {
    return api.put(
      `/api/polls/entry/${pk}/default_settings/`,
      hydrateDefaultSettings(data),
      formCfg(params, context)
    )
  },

  patchFirstStep(entry) {
    const { pk } = entry

    return api
      .patch(`/api/polls/entry/${pk}/`, hydrateFirstStep(entry))
      .then((response) => dehydrateFirstStep(response.data))
  },

  patchSecondStep(entry) {
    const { pk } = entry

    return api
      .patch(`/api/polls/entry/${pk}/`, hydrateSecondStep(entry))
      .then((response) => dehydrateSecondStep(response.data))
  },

  patchThirdStep(entry) {
    const { pk } = entry

    return api
      .patch(`/api/polls/entry/${pk}/`, hydrateThirdStep(entry))
      .then((response) => dehydrateThirdStep(response.data))
  },

  generateCsv(pk, expertPk, draft, params) {
    return api.get(
      urls.downloadCsv(pk, expertPk, {
        ...params,
        ...(draft ? { draft: true } : {}),
      })
    )
  },

  duplicate(pk, params, context) {
    return this.getDetail(pk, params, context).then((data) =>
      this.create({
        ...data,
        pk: null,
        region: _.find(data.allRegions, { pk: data.region }),
      })
    )
  },

  duplicateWithAnswers(pk, params, context) {
    return api.put(
      `/api/polls/entry/${pk}/duplicate_with_answers/`,
      {},
      formCfg(params, context)
    )
  },

  transfer(pk, toExpert, withExpertsAnswers = [], params, context) {
    return api.put(
      `/api/polls/entry/${pk}/transfer/`,
      {
        toExpert,
        withExpertsAnswers,
      },
      formCfg(params, context)
    )
  },

  generatePdf(pk, showDrafts) {
    return api.post(
      `/api/polls/entry/${pk}/pdf/`,
      {},
      formCfg({}, { showDrafts })
    )
  },

  getPdf(pk, showDrafts) {
    return api.get(`/api/polls/entry/${pk}/pdf/`, formCfg({}, { showDrafts }))
  },

  getDoi(pk) {
    return api
      .get(`/api/polls/entry/${pk}/doi/`)
      .then((response) =>
        _.map(response.data, (entryDoi) => doiDehydrate(entryDoi))
      )
  },

  getLastDoi(pk) {
    return api
      .get(`/api/polls/entry/${pk}/doi/last/`)
      .then((response) =>
        _.isEmpty(response.data) ? null : doiDehydrate(response.data)
      )
  },

  requestDoi(pk, data = {}) {
    return api
      .post(`/api/polls/entry/${pk}/doi/`, data)
      .then((response) => doiDehydrate(response.data))
  },

  approveDoi(entryPk, doiPk) {
    return api
      .put(`/api/polls/entry/${entryPk}/doi/${doiPk}/approve/`)
      .then((response) => doiDehydrate(response.data))
  },

  completeDoi(entryPk, doiPk, data) {
    return api
      .put(`/api/polls/entry/${entryPk}/doi/${doiPk}/complete/`, data)
      .then((response) => doiDehydrate(response.data))
  },

  deleteDoi(entryPk, doiPk) {
    return api
      .delete(`/api/polls/entry/${entryPk}/doi/${doiPk}/`)
      .then((response) => doiDehydrate(response.data))
  },

  getListWithPendingDoi() {
    return api
      .get('/api/polls/entry/with_pending_doi/')
      .then(({ data }) =>
        _.map(data, (entry) =>
          _.merge({}, entry, { lastDoi: doiDehydrate(entry.lastDoi) })
        )
      )
  },

  getListWithMapRequests() {
    return api
      .get('/api/polls/entry/with_map_requests/')
      .then(({ data }) => data)
  },

  getListWithTagSuggestions() {
    return api
      .get('/api/polls/entry/with_tag_suggestions/')
      .then(({ data }) => data)
  },

  notifyAboutInvitation(invitedExpertEmail, entryPk) {
    return api.post(`/api/polls/entry/${entryPk}/invite/`, {
      email: invitedExpertEmail,
    })
  },

  getEntryReferences(entryPk, params, context) {
    return api
      .get(`/api/polls/entry/${entryPk}/references/`, formCfg(params, context))
      .then(({ data }) => referenceDehydrate(data))
  },

  getMyReferences(params, context) {
    return api
      .get(`/api/polls/my_references/`, formCfg(params, context))
      .then(({ data }) => referenceDehydrate(data))
  },

  createReference(entryPk, params) {
    return api
      .post(`/api/polls/entry/${entryPk}/references/`, params)
      .then(({ data }) => referenceHydrate(data))
  },

  createAnswerSetReference(params) {
    return api
      .post(`/api/polls/answers_references/`, params)
      .then(({ data }) => answerSetReferenceHydrate(data))
  },

  getAnswersReferences(entryPk, params, context) {
    return api
      .get(
        `/api/polls/entry/${entryPk}/answers_references/`,
        formCfg(params, context)
      )
      .then(({ data }) => referenceDehydrate(data))
  },

  getAnswerReferencesByPk(answersetPk, params) {
    return api
      .get(`/api/polls/answers_references/?answerset=${answersetPk}`, params)
      .then(({ data }) => referenceDehydrate(data))
  },

  getBibliography(searchQuery, params, context) {
    return api
      .get(
        `/api/polls/bibliography/?title_or_author=${searchQuery}`,
        formCfg(params, context)
      )
      .then(({ data }) => referenceDehydrate(data))
  },

  getReferenceTypes(params, context) {
    return api
      .get(`/api/polls/reference_types`, formCfg(params, context))
      .then(({ data }) => referenceTypesDehydrate(data))
  },

  getGoogleRef(query, params) {
    return api
      .get(getGoogleBooksUrl(query), params)
      .then(({ data }) => cleanGoogleBooks(data))
  },

  getCrossRef(query, params) {
    return api
      .get(getCrossRefUrl(query), params)
      .then(({ data }) => cleanCrossRef(data))
  },

  createBibliography(params) {
    return api
      .post(`/api/polls/bibliography/`, params)
      .then(({ data }) => bibliographyHydrate(data))
  },

  updateBibliography(pk, params) {
    return api
      .put(`/api/polls/bibliography/${pk}/`, params)
      .then(({ data }) => bibliographyHydrate(data))
  },

  deleteEntryReference(entryReferencePk) {
    return api.delete(`/api/polls/references/${entryReferencePk}/`)
  },

  deleteAnswerReference(answerReferencePk) {
    return api.delete(`/api/polls/answers_references/${answerReferencePk}/`)
  },

  getEntrySubReferences(entryReferencePk, params, context) {
    return api
      .get(
        `/api/polls/subreferences/?entry_reference=${entryReferencePk}`,
        formCfg(params, context)
      )
      .then(({ data }) => entrySubReference(data))
  },

  createEntrySubReference(params) {
    return api
      .post(`/api/polls/subreferences/`, params)
      .then(({ data }) => entrySubReferenceHydrate(data))
  },

  updateEntrySubReference(subRefPk, params) {
    return api
      .put(`/api/polls/subreferences/${subRefPk}/`, params)
      .then(({ data }) => entrySubReferenceHydrate(data))
  },

  deleteEntrySubReference(entrySubReferencePk) {
    return api.delete(`/api/polls/subreferences/${entrySubReferencePk}/`)
  },

  getAnswerSetSubReferences(answerSetPk, params, context) {
    return api
      .get(
        `/api/polls/answers_subreferences/?answerset_reference=${answerSetPk}`,
        formCfg(params, context)
      )
      .then(({ data }) => entrySubReference(data))
  },

  createAnswerSetSubReference(params) {
    return api
      .post(`/api/polls/answers_subreferences/`, params)
      .then(({ data }) => answerSetSubReferenceHydrate(data))
  },

  updateAnswerSetSubReference(subRefPk, params) {
    return api
      .put(`/api/polls/answers_subreferences/${subRefPk}/`, params)
      .then(({ data }) => answerSetSubReferenceHydrate(data))
  },

  deleteAnswerSetSubReference(answerSetSubReferencePk) {
    return api.delete(
      `/api/polls/answers_subreferences/${answerSetSubReferencePk}/`
    )
  },

  getBibtexFile(entryPk) {
    setTimeout(() => {
      const response = {
        content: downloadBib(entryPk),
      }
      window.open(response.content)
    }, 100)
  },
}

function prepareDependencies({ entryTags, gisTags, experts, dataSources }) {
  const entryTagsByPk = _.keyBy(entryTags, 'pk')
  const gisTagsByPk = _.keyBy(gisTags, 'pk')
  const expertsByPk = _.keyBy(experts, 'pk')
  const dataSourcesByPk = _.keyBy(dataSources, 'pk')

  return { entryTagsByPk, gisTagsByPk, expertsByPk, dataSourcesByPk }
}

function dehydrateAndPopulate(
  {
    pk,
    name,
    description,
    altNames,
    tags,
    yearFrom,
    yearTo,
    region,
    createdByExpert,
    publishedContributors,
    entrySource,
    dataSource,
    externalUrl,
    secondarySource,
    expertSource,
    supervisedBy,
    dateUpdated,
    poll,
    belongsToExternalPoll,
  },
  { entryTagsByPk, gisTagsByPk, expertsByPk, dataSourcesByPk }
) {
  return {
    pk,
    description: description || '',
    name: nameDehydrate(name),
    alternativeNames: _.map(altNames, nameDehydrate),
    yearFrom: Math.abs(yearFrom),
    yearFromSign: yearFrom < 0,
    yearTo: Math.abs(yearTo),
    yearToSign: yearTo < 0,
    entryTags: _.compact(_.map(tags, (tagPk) => entryTagsByPk[tagPk])),
    publishedContributors: _.compact(
      _.map(publishedContributors, (expertPk) => expertsByPk[expertPk])
    ),
    createdByExpert: expertsByPk[createdByExpert],
    entrySource: entrySourceDehydrate(entrySource),
    dataSource: dataSourcesByPk[dataSource],
    externalUrl,
    secondarySource: secondarySource
      ? secondarySourceDehydrate(secondarySource)
      : null,
    expertSource: expertSource ? expertsByPk[expertSource] : null,
    supervisedBy: supervisedBy ? expertsByPk[supervisedBy] : null,
    region: regionDehydrateAndPopulate(region, { gisTagsByPk }),
    dateUpdated,
    poll,
    belongsToExternalPoll,
  }
}

export function dehydrate({
  pk,
  name,
  archived,
  description,
  altNames,
  tags,
  yearFrom,
  yearTo,
  region,
  branches,
  allRegions,
  createdByExpert,
  contributors,
  entrySource,
  dataSource,
  externalUrl,
  secondarySource,
  expertSource,
  supervisedBy,
  poll,
  dateUpdated,
  lastDoi,
  attachmentsCount,
  authorHonorarium,
  belongsToExternalPoll,
}) {
  const dehydratedContributors = _.map(contributors, expertDehydrate)

  return {
    pk,
    description: description || '',
    name: nameDehydrate(name),
    archived,
    alternativeNames: _.map(altNames, nameDehydrate),
    yearFrom: Math.abs(yearFrom),
    yearFromSign: yearFrom < 0,
    yearTo: Math.abs(yearTo),
    yearToSign: yearTo < 0,
    branches: branches,
    region: region,
    entryTags: _.map(tags, entryTagDehydrate),
    contributors: dehydratedContributors,
    publishedContributors: _.filter(
      dehydratedContributors,
      (expert) => expert.published
    ),
    createdByExpert: createdByExpert ? expertDehydrate(createdByExpert) : null,
    entrySource: entrySourceDehydrate(entrySource),
    dataSource: dataSourceDehydrate(dataSource),
    externalUrl,
    secondarySource: secondarySource
      ? secondarySourceDehydrate(secondarySource)
      : null,
    expertSource: expertSource ? expertDehydrate(expertSource) : null,
    supervisedBy: supervisedBy ? expertDehydrate(supervisedBy) : null,
    allRegions: _.keyBy(_.map(allRegions, regionDehydrate), 'pk'),
    poll,
    dateUpdated,
    lastDoi: _.isEmpty(lastDoi) ? null : doiDehydrate(lastDoi),
    attachmentsCount,
    authorHonorarium,
    belongsToExternalPoll,
  }
}

export function listDehydrate({
  pk,
  name,
  description,
  altNames,
  tags,
  yearFrom,
  yearTo,
  region,
  createdByExpert,
  entrySource,
  dataSource,
  externalUrl,
  secondarySource,
  expertSource,
  supervisedBy,
  dateUpdated,
  publishedContributors,
  poll,
  belongsToExternalPoll,
}) {
  return {
    pk,
    description: description || '',
    name: nameDehydrate(name),
    alternativeNames: _.map(altNames, nameDehydrate),
    yearFrom: Math.abs(yearFrom),
    yearFromSign: yearFrom < 0,
    yearTo: Math.abs(yearTo),
    yearToSign: yearTo < 0,
    entryTags: tags,
    publishedContributors,
    createdByExpert,
    entrySource: entrySourceDehydrate(entrySource),
    dataSource,
    externalUrl,
    secondarySource: secondarySource
      ? secondarySourceDehydrate(secondarySource)
      : null,
    expertSource,
    supervisedBy,
    region: region,
    dateUpdated,
    poll,
    belongsToExternalPoll,
  }
}

export function hydrate({
  name,
  description,
  alternativeNames,
  entryTags,
  region,
  yearFromSign,
  yearFrom,
  yearToSign,
  yearTo,
  branches,
  createdBy,
  poll,
  entrySource,
  dataSource,
  otherDataSourceName,
  secondarySource,
  otherSecondarySourceName,
  entrySourceExpertPk,
  externalUrl,
  belongsToExternalPoll,
}) {
  return {
    poll,
    ...entrySourceParams(
      entrySource,
      secondarySource,
      otherSecondarySourceName,
      entrySourceExpertPk
    ),
    dataSource: dataSourceHydrate(dataSource, otherDataSourceName),
    name: nameHydrate(name),
    description,
    externalUrl,
    createdBy,
    altNames: _.map(_.reject(alternativeNames, _.isEmpty), nameHydrate),
    tags: _.map(entryTags, 'pk'),
    yearFrom: combineYear(yearFrom, yearFromSign),
    yearTo: combineYear(yearTo, yearToSign),
    region: regionHydrate(region),
    branches: branches,
    belongsToExternalPoll,
  }
}

function entrySourceParams(
  entrySource,
  secondarySource,
  otherSecondarySourceName,
  entrySourceExpertPk
) {
  let result = {
    entrySource: entrySourceHydrate(entrySource),
  }

  switch (entrySource) {
    case 'secondary':
      result.secondarySource = hydrateSecondarySource(
        secondarySource,
        otherSecondarySourceName
      )
      break

    case 'expert':
      result.expertSource = entrySourceExpertPk
      break

    case 'supervised':
      result.supervisedBy = entrySourceExpertPk
      break
    default:
      break
  }

  return result
}

function hydrateDefaultSettings({
  region,
  yearFromSign,
  yearFrom,
  yearToSign,
  yearTo,
  branches,
  applyToExistingAnswerSets,
}) {
  return {
    yearFrom: combineYear(yearFrom, yearFromSign),
    yearTo: combineYear(yearTo, yearToSign),
    region: region,
    branches: branches,
    applyToExistingAnswerSets,
  }
}

function hydrateFirstStep({
  name,
  description,
  alternativeNames,
  entrySource,
  dataSource,
  otherDataSourceName,
  secondarySource,
  otherSecondarySourceName,
  entrySourceExpertPk,
  externalUrl,
  belongsToExternalPoll,
}) {
  return {
    ...entrySourceParams(
      entrySource,
      secondarySource,
      otherSecondarySourceName,
      entrySourceExpertPk
    ),
    dataSource: dataSourceHydrate(dataSource, otherDataSourceName),
    name,
    description,
    externalUrl,
    altNames: _.map(_.reject(alternativeNames, _.isEmpty), nameHydrate),
    belongsToExternalPoll,
  }
}

function dehydrateFirstStep({
  name,
  description,
  altNames,
  entrySource,
  dataSource,
  secondarySource,
  expertSource,
  supervisedBy,
  externalUrl,
  belongsToExternalPoll,
}) {
  return {
    name,
    description: description || '',
    alternativeNames: _.map(altNames, nameDehydrate),
    entrySource: entrySourceDehydrate(entrySource),
    dataSource,
    secondarySource: secondarySource
      ? secondarySourceDehydrate(secondarySource)
      : null,
    expertSource,
    supervisedBy,
    externalUrl,
    belongsToExternalPoll,
  }
}

function hydrateSecondStep({ entryTags }) {
  return {
    tags: _.map(entryTags, 'pk'),
  }
}

function dehydrateSecondStep({ tags }) {
  return {
    entryTags: _.map(tags, entryTagDehydrate),
  }
}

function hydrateThirdStep({ region }) {
  return {
    region: region,
  }
}

function dehydrateThirdStep({ region }) {
  return {
    region: region,
  }
}

function dehydrateStats(stats) {
  return stats
}

export default entry
